import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Clear all module-level caches (call on logout).
 * Note: points config is NOT cached on the client — daily caps and
 * action values are enforced by the database (server-authoritative).
 */
export function clearAllGamificationCaches() {
  levelsCache = null;
}

// ─── Query Keys (single source of truth) ────────────

export const GAMIFICATION_KEYS = {
  summary: (userId: string) => ['community-gamification-summary', userId] as const,
  leaderboard: (period: string) => ['community-leaderboard', period] as const,
  displayInfo: (userId: string) => ['user-display-info', userId] as const,
};

/**
 * Single source of truth for the Community Levels query key.
 * All consumers (sidebar gating, ranking, desafios, eventos, fórum)
 * MUST use this key so that one fetch is shared across the whole app
 * and a single `invalidateQueries({ queryKey: LEVELS_QUERY_KEY })`
 * refreshes everything after admin edits.
 */
export const LEVELS_QUERY_KEY = ['community-levels'] as const;

export function invalidateGamification(qc: QueryClient, userId?: string) {
  if (userId) {
    qc.invalidateQueries({ queryKey: ['community-gamification-summary', userId] });
    qc.invalidateQueries({ queryKey: ['user-display-info', userId] });
  }
  qc.invalidateQueries({ queryKey: ['community-leaderboard'] });
}

/** Invalidate ALL user-display-info queries (e.g. after admin changes a badge icon) */
export function invalidateAllDisplayInfo(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['user-display-info'] });
}

// ─── Award / Revoke Points ──────────────────────────

export async function awardPoints(userId: string, action: string, referenceId?: string): Promise<number> {
  // Cérebro awards points from trusted domain events. Browser-authored point
  // mutations are deliberately ignored to preserve the anti-farm contract.
  void userId; void action; void referenceId;
  return 0;
}

/**
 * Award points AND invalidate the gamification caches in a single call.
 * Use this anywhere a user-facing point change must reflect immediately
 * in `UserPointsDisplay`, ranking, level progression and breakdown.
 *
 * Returns the points granted (0 if action disabled or daily cap hit).
 */
export async function awardPointsAndInvalidate(
  qc: QueryClient,
  userId: string,
  action: string,
  referenceId?: string,
): Promise<number> {
  const pts = await awardPoints(userId, action, referenceId);
  if (pts > 0) invalidateGamification(qc, userId);
  return pts;
}

export async function revokePoints(userId: string, action: string, referenceId: string): Promise<void> {
  void userId; void action; void referenceId;
}

/**
 * Award the per-tab bonus configured by admins in Comunidade → Abas.
 *
 * Fire-and-forget by design: bonus is purely additive on top of the standard
 * action points (create_post, create_reply, chat_message). The RPC enforces
 * idempotency, the daily 20-pt engagement cap, and the chat 30s cooldown.
 */
export async function awardTabBonus(
  userId: string,
  tabSlug: string,
  referenceId: string,
): Promise<number> {
  if (!tabSlug || !referenceId) return 0;
  void userId; void tabSlug; void referenceId;
  return 0;
}

/**
 * Database-authoritative reconciliation for reversible actions.
 *
 * The backend queries the actual base tables (completions, ratings, progress)
 * to decide whether the points entry should exist — the frontend does NOT
 * pass any boolean. This eliminates race conditions, stale state, and
 * out-of-order async issues.
 *
 * Idempotent: safe to call repeatedly. The result always reflects reality.
 *
 * @returns the points value (0 if action shouldn't exist or is disabled)
 */
export async function reconcileActionPoints(
  userId: string,
  action: string,
  referenceId: string,
): Promise<number> {
  void userId; void action; void referenceId;
  return 0;
}

// ─── Levels ─────────────────────────────────────────

export interface CommunityLevel {
  id: string;
  name: string;
  min_points: number;
  icon: string;
  icon_url: string | null;
  color: string | null;
  color_secondary: string | null;
  position: number;
  created_at: string;
}

let levelsCache: CommunityLevel[] | null = null;
let levelsInflight: Promise<CommunityLevel[]> | null = null;

const LEVELS_LS_KEY = 'forja:community_levels:v1';
const LEVELS_TTL_MS = 60 * 60 * 1000; // 1 hour — admin-only data, raríssimo mudar

interface PersistedLevels { ts: number; data: CommunityLevel[] }

function readPersisted(): CommunityLevel[] | null {
  try {
    const raw = localStorage.getItem(LEVELS_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedLevels;
    if (!parsed?.ts || Date.now() - parsed.ts > LEVELS_TTL_MS) return null;
    return parsed.data ?? null;
  } catch { return null; }
}

function writePersisted(data: CommunityLevel[]) {
  try { localStorage.setItem(LEVELS_LS_KEY, JSON.stringify({ ts: Date.now(), data })); } catch { /* ignore */ }
}

export async function fetchLevels(): Promise<CommunityLevel[]> {
  if (levelsCache) return levelsCache;
  const persisted = readPersisted();
  if (persisted) {
    levelsCache = persisted;
    return persisted;
  }
  // Dedup concurrent callers within the same tab
  if (levelsInflight) return levelsInflight;
  levelsInflight = (async () => {
    const { data, error } = await (supabase as any).rpc('get_gamification_home', { p_history_limit: 1 });
    if (error) throw error;
    const result = ((data?.levels ?? []) as any[]).map(level => ({
      id: level.id, name: level.name, min_points: level.min_points,
      icon: level.icon, icon_url: null, color: level.color ?? null,
      color_secondary: null, position: level.position, created_at: new Date(0).toISOString(),
    })) as CommunityLevel[];
    levelsCache = result;
    writePersisted(result);
    setTimeout(() => { levelsCache = null; }, LEVELS_TTL_MS);
    return result;
  })();
  try { return await levelsInflight; }
  finally { levelsInflight = null; }
}

export function clearLevelsCache() {
  levelsCache = null;
  try { localStorage.removeItem(LEVELS_LS_KEY); } catch { /* ignore */ }
}

export function getUserLevel(totalPoints: number, levels: CommunityLevel[]): CommunityLevel | null {
  let current: CommunityLevel | null = null;
  for (const l of levels) {
    if (l.min_points <= totalPoints) current = l;
  }
  return current;
}

// ─── User Display Info (unified RPC) ────────────────

export interface UserBadge {
  name: string;
  icon: string;
  icon_url: string | null;
  color: string | null;
  color_secondary: string | null;
  priority: number;
  badge_type: 'admin' | 'medal';
}

export interface UserDisplayInfo {
  badges: UserBadge[];
  /**
   * Subset of `CommunityLevel` returned by `get_user_display_info`.
   * Tolerant to extra fields (color/position/created_at) so the RPC
   * can evolve without forcing frontend changes.
   */
  level: Pick<CommunityLevel, 'id' | 'name' | 'icon' | 'icon_url' | 'min_points' | 'color' | 'color_secondary'> | null;
  total_points: number;
  name_color: string | null;
  name_color_secondary: string | null;
}

export async function fetchUserDisplayInfo(userId: string): Promise<UserDisplayInfo> {
  const { data, error } = await (supabase as any).rpc('list_gamification_leaderboard', { p_page: 1, p_limit: 100 });
  if (error) throw error;
  const entry = data?.items?.find((item: any) => item.user_id === userId);
  return {
    badges: [],
    level: entry?.level ? { id: entry.level.key, name: entry.level.name, icon: entry.level.icon, icon_url: null, min_points: 0, color: entry.level.color, color_secondary: null } : null,
    total_points: entry?.points ?? 0,
    name_color: entry?.level?.color ?? null,
    name_color_secondary: null,
  };
}

function parseDisplayInfo(result: any): UserDisplayInfo {
  return {
    badges: result?.badges ?? [],
    level: result?.level ?? null,
    total_points: result?.total_points ?? 0,
    name_color: result?.name_color ?? null,
    name_color_secondary: result?.name_color_secondary ?? null,
  };
}

/** Fetch display info for multiple users in a single RPC call and populate query cache */
export async function fetchBatchUserDisplayInfo(
  userIds: string[],
  queryClient: QueryClient,
): Promise<void> {
  if (userIds.length === 0) return;

  // Filter out IDs already cached and fresh
  const uncached = userIds.filter(id => {
    const state = queryClient.getQueryState(GAMIFICATION_KEYS.displayInfo(id));
    if (!state?.dataUpdatedAt) return true;
    return Date.now() - state.dataUpdatedAt > DISPLAY_INFO_STALE;
  });
  if (uncached.length === 0) return;

  await Promise.all(uncached.map(async uid => {
    try { queryClient.setQueryData(GAMIFICATION_KEYS.displayInfo(uid), await fetchUserDisplayInfo(uid)); }
    catch { /* keep the surrounding UI available */ }
  }));
}

export const DISPLAY_INFO_STALE = 5 * 60_000; // 5 minutes

// ─── User Gamification Summary ──────────────────────

export interface GamificationSummary {
  totalPoints: number;
  rankPosition: number;
  rankTotal: number;
  currentLevel: CommunityLevel | null;
  nextLevel: CommunityLevel | null;
  pointsToNext: number | null;
}

export async function fetchUserGamificationSummary(userId: string): Promise<GamificationSummary> {
  void userId;
  const { data, error } = await (supabase as any).rpc('get_gamification_home', { p_history_limit: 10 });

  if (error || !data) {
    console.error('Error fetching gamification summary:', error);
    return { totalPoints: 0, rankPosition: 1, rankTotal: 1, currentLevel: null, nextLevel: null, pointsToNext: null };
  }

  const summary = data as any;
  const totalPoints = summary.summary?.points ?? 0;
  const levels: CommunityLevel[] = (summary.levels ?? []).map((l: any) => ({
    id: l.id,
    name: l.name,
    icon: l.icon,
    icon_url: l.icon_url ?? null,
    image_url: l.image_url ?? null,
    color: l.color ?? null,
    color_secondary: l.color_secondary ?? null,
    min_points: l.min_points,
    position: l.position,
  }));

  const currentLevel = getUserLevel(totalPoints, levels);
  const currentIdx = currentLevel ? levels.indexOf(currentLevel) : -1;
  const nextLevel = currentIdx >= 0 ? levels[currentIdx + 1] ?? null : levels[0] ?? null;

  return {
    totalPoints,
    rankPosition: summary.summary?.position ?? 1,
    rankTotal: 1,
    currentLevel,
    nextLevel,
    pointsToNext: nextLevel ? nextLevel.min_points - totalPoints : null,
  };
}

// ─── Unified Points Breakdown (single source of truth) ─

export interface PointsBreakdownEntry {
  action: string;
  entries: number;
  total_points: number;
}

export interface PointsRecentEntry {
  action: string;
  points: number;
  reference_id: string | null;
  created_at: string;
}

export interface UserPointsBreakdown {
  total_points: number;
  today_engagement_points: number;
  daily_cap: number;
  rank_position: number;
  rank_total: number;
  breakdown: PointsBreakdownEntry[];
  recent: PointsRecentEntry[];
  levels: CommunityLevel[];
}

/**
 * Single source of truth for any UI showing a user's points.
 * Returns total, per-action breakdown, daily cap progress,
 * recent 10 entries, ranking and level data — in one RPC call.
 */
export async function fetchUserPointsBreakdown(userId: string): Promise<UserPointsBreakdown | null> {
  void userId;
  const { data, error } = await (supabase as any).rpc('get_gamification_home', { p_history_limit: 10 });
  if (error) {
    console.error('[fetchUserPointsBreakdown]', error);
    return null;
  }
  const r = data as any;
  return {
    total_points: r?.summary?.points ?? 0,
    today_engagement_points: 0,
    daily_cap: r?.daily_cap ?? 20,
    rank_position: r?.summary?.position ?? 1,
    rank_total: 1,
    breakdown: [],
    recent: (r?.history ?? []).map((item: any) => ({
      action: item.rule_key, points: item.points_delta, reference_id: null, created_at: item.occurred_at,
    })),
    levels: (r?.levels ?? []).map((level: any) => ({
      id: level.id, name: level.name, min_points: level.min_points, icon: level.icon,
      icon_url: null, color: level.color ?? null, color_secondary: null,
      position: level.position, created_at: new Date(0).toISOString(),
    })),
  };
}

// ─── Admin Audit ────────────────────────────────────

export interface OrphanAuditRow {
  action: string;
  orphan_count: number;
  total_points: number;
}

export async function fetchOrphanAudit(): Promise<OrphanAuditRow[]> {
  return [];
}

export async function runOrphanCleanup(): Promise<number> {
  return 0;
}

// ─── Leaderboard ────────────────────────────────────

export interface LeaderboardEntry {
  user_id: string;
  total_points: number;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export async function fetchLeaderboard(limit = 10, startDate?: string): Promise<LeaderboardEntry[]> {
  void startDate;
  const { data, error } = await (supabase as any).rpc('list_gamification_leaderboard', { p_page: 1, p_limit: limit });
  if (error) throw error;
  return (data?.items ?? []).map((entry: any) => ({
    user_id: entry.user_id, total_points: entry.points,
    display_name: entry.display_name, avatar_url: entry.avatar_url, bio: null,
  }));
}

/** Check if user has a specific permission via admin badges */
export async function userHasPermission(userId: string, permission: string): Promise<boolean> {
  void userId; void permission;
  const { data } = await (supabase as any).rpc('current_forjahub_access');
  const access = Array.isArray(data) ? data[0] : data;
  return access?.role === 'admin' || access?.role === 'staff';
}

